import { useEffect, useRef } from 'react';
import type { Member } from '../../api/client';
import { Avatar } from '../AppShell';

interface MentionPickerProps {
  members: Member[];
  query: string;
  onSelect: (member: Member) => void;
  onClose: () => void;
}

export default function MentionPicker({ members, query, onSelect, onClose }: MentionPickerProps) {
  const ref = useRef<HTMLDivElement>(null);

  const filtered = members.filter((m) => {
    const q = query.toLowerCase();
    return (
      m.user.name.toLowerCase().includes(q) ||
      m.user.email.toLowerCase().includes(q)
    );
  });

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        onClose();
      }
    }
    function handleEscape(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onClose]);

  if (filtered.length === 0) {
    return null;
  }

  return (
    <div
      ref={ref}
      className="absolute bottom-full left-0 mb-[var(--sp-1)] w-full max-h-[200px] overflow-y-auto rounded-[var(--radius-md)] z-10"
      style={{
        background: 'var(--color-surface-neutral-default)',
        border: '1px solid var(--color-border-neutral-subtle-default)',
        boxShadow: 'var(--shadow-2)',
      }}
    >
      {filtered.map((member) => (
        <button
          key={member.id}
          onClick={() => {
            onSelect(member);
            onClose();
          }}
          className="w-full flex items-center gap-[var(--sp-2)] px-[var(--sp-3)] py-[var(--sp-2)] cursor-pointer border-0 bg-transparent text-left transition-colors duration-[120ms]"
          onMouseEnter={(e) =>
            (e.currentTarget.style.background = 'var(--color-surface-neutral-medium-hover)')
          }
          onMouseLeave={(e) =>
            (e.currentTarget.style.background = 'transparent')
          }
        >
          <Avatar
            name={member.user.name}
            avatarUrl={member.user.avatar_url ?? undefined}
            size={24}
          />
          <div className="flex flex-col min-w-0">
            <span
              className="text-[13px] font-semibold truncate"
              style={{ color: 'var(--color-text-neutral-strong)' }}
            >
              {member.user.name}
            </span>
            <span
              className="text-[13px] truncate"
              style={{ color: 'var(--color-text-neutral-subtle)' }}
            >
              {member.user.email}
            </span>
          </div>
        </button>
      ))}
    </div>
  );
}
